import * as React from 'react';
import type { InspectionGroupBy } from '@relay-towing/api';
import {
  ListColumnRow,
  ListToolbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
  TextLink,
  type ListMode,
} from '@relay-towing/ui';
import { Link, useNavigate } from 'react-router-dom';
import type {
  InspectionStatus,
  MyInspection,
} from '../../api/truckInspections';
import { useMember } from '../../hooks/useMember';
import { usePagedList, type PagedList } from '../../hooks/usePagedList';
import {
  dayLabel,
  shortWhen,
  statusLabel,
  statusTone,
  truckName,
} from '../../lib/inspections';
import { PagedRows } from '../shell/PagedRows';
import { FunctionPage } from '../functions/FunctionPage';
import { StartInspectionButton } from './StartInspectionButton';

type View = 'open' | 'closed';

/**
 * Open is what needs attention: issues found, or a run not yet filed.
 * Closed is what passed (the owner, 2026-10-28). A quick toggle over the
 * list, as Invoices has Current and Archived.
 */
const VIEW_STATUSES: Record<View, InspectionStatus[]> = {
  open: ['ISSUES_FOUND', 'IN_PROGRESS'],
  closed: ['PASSED'],
};

const subjectOf = (row: MyInspection) =>
  row.subjectLabel || truckName(row.truck ?? {});

/**
 * Inspections, from the Functions tab. No Figma frame exists for it: the
 * layout is inferred, and a designer's frame replaces it. It follows the
 * old screen (screens/inspections/InspectionsScreen.tsx), the dashboard's
 * inspections page and the impounds and invoices lists beside it.
 *
 * The filed reports, grouped by result on the server, worst first: the
 * member's own, or the company's with "View all inspections", as the
 * server decides. With "Run inspections" (`inspections.create`), New picks
 * a checklist and runs it. The checklists themselves are managed on the
 * dashboard.
 *
 * The reports are the dashboard's grouped list (Figma 45-5523): a band per
 * result and a row per report, or the same reports as a table. The search
 * looks through the reports already loaded, as the dashboard's does; the
 * server has no search of its own for them.
 */
export function InspectionsScreen() {
  const navigate = useNavigate();
  const member = useMember();
  const [view, setView] = React.useState<View>('open');
  const reports = usePagedList<MyInspection>(
    'mobile-inspections',
    '/inspections',
    {
      groupBy: 'status' satisfies InspectionGroupBy,
      status: VIEW_STATUSES[view],
    }
  );
  // Each row names who filed it only where they are not all the member's.
  const company = member.can('inspections.view');
  const [mode, setMode] = React.useState<ListMode>('list');
  const [search, setSearch] = React.useState('');
  const needle = search.trim().toLowerCase();
  // While a search narrows the loaded reports, a band counts what it shows
  // rather than every page, or it would head rows it does not have.
  const shownReports: PagedList<MyInspection> = needle
    ? {
        ...reports,
        meta: undefined,
        items: reports.items.filter((row) =>
          [subjectOf(row), row.templateName, row.submittedBy?.name].some(
            (field) => field?.toLowerCase().includes(needle)
          )
        ),
      }
    : reports;
  const open = (row: MyInspection) => navigate(`/inspections/report/${row.id}`);

  return (
    <FunctionPage
      id="inspections"
      title="Inspections"
      actions={
        member.can('inspections.create') ? <StartInspectionButton /> : undefined
      }
    >
      <Tabs value={view} onValueChange={(next) => setView(next as View)}>
        <TabsList fullWidth>
          <TabsTrigger value="open" fullWidth>
            Open
          </TabsTrigger>
          <TabsTrigger value="closed" fullWidth>
            Closed
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <PagedRows
        list={shownReports}
        empty={needle ? 'No reports match' : 'No inspections yet'}
        groups={{
          of: (row) => row.status,
          label: (status) => statusLabel(status as InspectionStatus),
          tone: (status) => statusTone(status as InspectionStatus),
        }}
        toolbar={
          <ListToolbar
            mode={mode}
            onModeChange={setMode}
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by subject or name"
            searchLabel="Search the reports already loaded"
          />
        }
        table={
          mode === 'table'
            ? (rows) => (
                <Table shape="band">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Subject</TableHead>
                      <TableHead>Checklist</TableHead>
                      {company ? <TableHead>Filed by</TableHead> : null}
                      <TableHead>When</TableHead>
                      <TableHead>Result</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow
                        key={row.id}
                        interactive
                        onClick={() => open(row)}
                      >
                        <TableCell>
                          {/* The row tap is the convenience; the link is
                                the way in from a keyboard, as on the
                                dashboard's table. */}
                          <TextLink asChild>
                            <Link
                              to={`/inspections/report/${row.id}`}
                              onClick={(event) => event.stopPropagation()}
                            >
                              {subjectOf(row)}
                            </Link>
                          </TextLink>
                        </TableCell>
                        <TableCell>{row.templateName}</TableCell>
                        {company ? (
                          <TableCell>{row.submittedBy?.name ?? '—'}</TableCell>
                        ) : null}
                        <TableCell>{shortWhen(row.submittedAt)}</TableCell>
                        <TableCell>
                          {/* Words, as the design's table has them. */}
                          {statusLabel(row.status)}
                          {row._count.defects > 0
                            ? ` · ${row._count.defects} defect${row._count.defects === 1 ? '' : 's'}`
                            : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
            : undefined
        }
      >
        {(row) => (
          <ListColumnRow
            key={row.id}
            lead={subjectOf(row)}
            trail={dayLabel(row.submittedAt)}
            onClick={() => open(row)}
          >
            {/* Who filed it where they are not all the member's own;
                  otherwise which checklist it was. */}
            {company
              ? (row.submittedBy?.name ?? row.templateName)
              : row.templateName}
          </ListColumnRow>
        )}
      </PagedRows>
    </FunctionPage>
  );
}
